import { type UseModalState } from "@src/lib/ui-react";
import { Box, Stack } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { I18N_NAMESPACES } from "@src/appV2/i18n/constants";
import { useTu } from "@src/appV2/i18n/useTu";
import { useKnockTokenContext } from "@src/appV2/Knock/context";
import { unregisterPushTokenInKnock } from "@src/appV2/Knock/push/api";
import { Button } from "@src/appV2/redesign/components/Button";
import { Dialog } from "@src/appV2/redesign/components/Dialog";
import { Text } from "@src/appV2/redesign/components/Text";
import {
  getStoredPushToken,
  removeStoredPushToken,
} from "@src/appV2/redesign/PushNotifications/pushTokenStorage";
import { logout } from "@src/modules/session";
import { useState } from "react";
// eslint-disable-next-line no-restricted-imports -- Dispatching logout thunk requires direct useDispatch
import { useDispatch } from "react-redux";

interface LogoutConfirmationDialogProps {
  modalState: UseModalState;
}

export function LogoutConfirmationDialog(props: LogoutConfirmationDialogProps) {
  const { modalState } = props;
  const dispatch = useDispatch();
  const { user, admin } = useSession();
  const workplaceUserId = user?._id;
  // KnockTokenProvider only mounts for non-admin workplace sessions, so for
  // admin sessions this resolves to constate's empty sentinel and the
  // unregister below is skipped via the `admin` early-return anyway.
  const { knockToken } = useKnockTokenContext();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { tu } = useTu(I18N_NAMESPACES.navigation);

  async function handleLogout() {
    setIsLoggingOut(true);

    try {
      // Admins never registered a push token under the impersonated user's
      // id (KnockTokenProvider only mounts for non-admin sessions), so
      // there's nothing to unregister.
      if (!admin && workplaceUserId) {
        const stored = getStoredPushToken(workplaceUserId);
        if (knockToken && stored) {
          // Awaited so the unregister completes while the JWT is still valid;
          // cognitoSignOut downstream invalidates auth.
          await unregisterPushTokenInKnock({
            token: stored.token,
            userId: workplaceUserId,
            knockToken,
          });
        }
        removeStoredPushToken(workplaceUserId);
      }

      await dispatch(logout());
      // Dialog unmounts when LOGOUT flips auth state; no closeModal needed.
    } finally {
      setIsLoggingOut(false);
    }
  }

  return (
    <Dialog
      showCloseButton
      modalState={modalState}
      title={tu("Confirm Log Out")}
      footerContent={
        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingX: theme.spacing(5),
            paddingY: theme.spacing(3),
          })}
        >
          <Button variant="outlined" disabled={isLoggingOut} onClick={modalState.closeModal}>
            {tu("Cancel")}
          </Button>
          <Button variant="danger" disabled={isLoggingOut} onClick={handleLogout}>
            {isLoggingOut ? tu("Logging out…") : tu("Log Out")}
          </Button>
        </Box>
      }
    >
      <Stack alignItems="center" spacing={3}>
        <Stack alignItems="center" spacing={1}>
          <Text>{tu("Are you sure you want to log out?")}</Text>
          <Text variant="body2" sx={(theme) => ({ color: theme.palette.text?.secondary })}>
            {tu("You will need to sign in again to access your account.")}
          </Text>
        </Stack>
      </Stack>
    </Dialog>
  );
}
